import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { activityApi, customerApi, userApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Search,
  Plus,
  MapPin,
  Calendar as CalendarIcon,
  Clock,
  User,
  Building2,
  Phone,
  Users,
  CheckCircle,
  Play,
  MoreVertical,
  Filter,
  Trash2,
  Eye
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const ACTIVITY_TYPES = [
  { value: 'visit', label: 'Site Visit', icon: MapPin, color: 'bg-blue-100 text-blue-700' },
  { value: 'meeting', label: 'Meeting', icon: Users, color: 'bg-purple-100 text-purple-700' },
  { value: 'call', label: 'Phone Call', icon: Phone, color: 'bg-green-100 text-green-700' },
  { value: 'task', label: 'Task', icon: CheckCircle, color: 'bg-orange-100 text-orange-700' },
  { value: 'followup', label: 'Follow-up', icon: Clock, color: 'bg-yellow-100 text-yellow-700' },
];

const PRIORITIES = [
  { value: 'high', label: 'High', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'low', label: 'Low', color: 'bg-green-100 text-green-700 border-green-200' },
];

const STATUSES = [
  { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
];

export default function ActivitiesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [newActivity, setNewActivity] = useState({
    type: 'visit',
    title: '',
    description: '',
    customer_id: '',
    assigned_to: '',
    priority: 'medium',
    scheduled_date: null,
    scheduled_time: '',
    duration_minutes: 60,
    meeting_type: 'in_person',
    agenda: '',
    notes: '',
  });

  const fetchData = async () => {
    try {
      const params = {};
      if (filterType !== 'all') params.type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterPriority !== 'all') params.priority = filterPriority;

      const [activitiesRes, customersRes] = await Promise.all([
        activityApi.getAll(params),
        customerApi.getAll({ status: 'active' }),
      ]);
      setActivities(activitiesRes.data);
      setCustomers(customersRes.data);

      if (user?.role !== 'salesperson') {
        const salespersonsRes = await userApi.getSalespersons();
        setSalespersons(salespersonsRes.data);
      }
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterType, filterStatus, filterPriority]);

  const handleCreateActivity = async () => {
    if (!newActivity.title || !newActivity.customer_id) {
      toast.error('Title and customer are required');
      return;
    }

    try {
      const activityData = {
        ...newActivity,
        scheduled_date: newActivity.scheduled_date ? format(newActivity.scheduled_date, 'yyyy-MM-dd') : null,
      };
      await activityApi.create(activityData);
      toast.success('Activity created successfully');
      setIsCreateOpen(false);
      setNewActivity({
        type: 'visit', title: '', description: '', customer_id: '', assigned_to: '',
        priority: 'medium', scheduled_date: null, scheduled_time: '', duration_minutes: 60,
        meeting_type: 'in_person', agenda: '', notes: '',
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create activity');
    }
  };

  const handleStartActivity = async (activityId) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await activityApi.checkIn(activityId, {
            activity_id: activityId,
            check_type: 'start',
            location: { lat: position.coords.latitude, lng: position.coords.longitude },
          });
          toast.success('Activity started');
          fetchData();
        } catch (err) {
          toast.error('Failed to start activity');
        }
      },
      () => toast.error('Could not get location')
    );
  };

  const handleCompleteActivity = async (activityId) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await activityApi.checkIn(activityId, {
            activity_id: activityId,
            check_type: 'end',
            location: { lat: position.coords.latitude, lng: position.coords.longitude },
          });
          toast.success('Activity completed');
          fetchData();
        } catch (err) {
          toast.error('Failed to complete activity');
        }
      },
      () => toast.error('Could not get location')
    );
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await activityApi.delete(activityId);
      toast.success('Activity deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const filteredActivities = activities.filter(activity => {
    const matchesSearch = activity.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          activity.customer_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || activity.type === activeTab;
    return matchesSearch && matchesTab;
  });

  const getTypeInfo = (type) => ACTIVITY_TYPES.find(t => t.value === type) || ACTIVITY_TYPES[0];
  const getPriorityColor = (priority) => PRIORITIES.find(p => p.value === priority)?.color || 'bg-gray-100';
  const getStatusColor = (status) => STATUSES.find(s => s.value === status)?.color || 'bg-gray-100';

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="activities-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>Activities</h1>
            <p className="text-sm text-gray-500 mt-1">Manage visits, meetings, calls, and tasks</p>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-activity-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Activity
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>Create Activity</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Activity Type */}
                <div>
                  <Label>Activity Type *</Label>
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {ACTIVITY_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.value}
                          className={`p-3 rounded-lg border-2 text-center transition-all ${
                            newActivity.type === type.value
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300'
                          }`}
                          onClick={() => setNewActivity({ ...newActivity, type: type.value })}
                        >
                          <Icon className="w-5 h-5 mx-auto mb-1" />
                          <span className="text-xs">{type.label.split(' ')[0]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label>Title *</Label>
                  <Input
                    value={newActivity.title}
                    onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                    placeholder="Activity title"
                    data-testid="activity-title-input"
                  />
                </div>

                <div>
                  <Label>Customer *</Label>
                  <Select value={newActivity.customer_id} onValueChange={(v) => setNewActivity({ ...newActivity, customer_id: v })}>
                    <SelectTrigger data-testid="activity-customer-select">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} ({c.city})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Priority</Label>
                    <Select value={newActivity.priority} onValueChange={(v) => setNewActivity({ ...newActivity, priority: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITIES.map(p => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {isAdmin && (
                    <div>
                      <Label>Assign To</Label>
                      <Select value={newActivity.assigned_to} onValueChange={(v) => setNewActivity({ ...newActivity, assigned_to: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select person" />
                        </SelectTrigger>
                        <SelectContent>
                          {salespersons.map(sp => (
                            <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {newActivity.scheduled_date ? format(newActivity.scheduled_date, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={newActivity.scheduled_date}
                          onSelect={(date) => setNewActivity({ ...newActivity, scheduled_date: date })}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Time</Label>
                    <Input
                      type="time"
                      value={newActivity.scheduled_time}
                      onChange={(e) => setNewActivity({ ...newActivity, scheduled_time: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={newActivity.description}
                    onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                    placeholder="Activity description..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)} className="flex-1">Cancel</Button>
                  <Button onClick={handleCreateActivity} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="activity-submit-btn">
                    Create Activity
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-100">
            <TabsTrigger value="all">All</TabsTrigger>
            {ACTIVITY_TYPES.map(type => (
              <TabsTrigger key={type.value} value={type.value} className="flex items-center gap-1">
                <type.icon className="w-4 h-4" />
                {type.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Filters */}
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search activities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  {PRIORITIES.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Activities List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : filteredActivities.length === 0 ? (
          <Card className="border-gray-200">
            <CardContent className="text-center py-12">
              <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No activities found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredActivities.map((activity) => {
              const typeInfo = getTypeInfo(activity.type);
              const TypeIcon = typeInfo.icon;
              return (
                <Card key={activity.id} className="border-gray-200 hover:border-blue-200 transition-all" data-testid={`activity-card-${activity.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className={`p-2.5 rounded-lg ${typeInfo.color}`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium text-gray-900">{activity.title}</h3>
                            <Badge className={getPriorityColor(activity.priority)}>{activity.priority}</Badge>
                            <Badge variant="outline" className={getStatusColor(activity.status)}>
                              {activity.status.replace('_', ' ')}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3.5 h-3.5" />
                              {activity.customer_name}
                            </span>
                            {activity.scheduled_date && (
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="w-3.5 h-3.5" />
                                {activity.scheduled_date}
                              </span>
                            )}
                            {activity.assigned_to_name && (
                              <span className="flex items-center gap-1">
                                <User className="w-3.5 h-3.5" />
                                {activity.assigned_to_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {activity.status === 'pending' && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleStartActivity(activity.id)}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Start
                          </Button>
                        )}
                        {activity.status === 'in_progress' && (
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handleCompleteActivity(activity.id)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Complete
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/activities/${activity.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {isAdmin && (
                              <DropdownMenuItem onClick={() => handleDeleteActivity(activity.id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
